import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RootState, AppDispatch } from '../store/store';
import {
  fetchSettings,
  fetchCurrencies,
  selectSettings,
  selectCurrencies,
  selectHospitalName,
  selectCurrency,
  selectCurrencySymbol,
  selectSettingsInitialized
} from '../store/slices/settingsSlice';
import { settingsApi } from '../lib/api';

interface SettingsContextType {
  hospitalName: string;
  hospitalLogo: string;
  currency: string;
  currencySymbol: string;
  dateFormat: string;
  timeFormat: string;
  settings: any;
  currencies: any[];
  initialized: boolean;
  refreshUserPreferences: () => Promise<void>;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const settings = useSelector(selectSettings);
  const currencies = useSelector(selectCurrencies);
  const initialized = useSelector(selectSettingsInitialized);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Use React Query for data fetching with proper caching - ONLY when authenticated
  const { data: userPreferences } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: async () => {
      const response = await settingsApi.getUserPreferences();
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: isAuthenticated // Only run query when authenticated
  });

  // Fetch settings only when authenticated and not initialized
  useEffect(() => {
    if (isAuthenticated && !initialized) {
      dispatch(fetchSettings()).unwrap().catch(console.error);
      dispatch(fetchCurrencies()).unwrap().catch(console.error);
    }
  }, [dispatch, initialized, isAuthenticated]);

  // Refresh functions with proper debouncing
  const refreshUserPreferences = useCallback(async () => {
    if (isAuthenticated) {
      await queryClient.invalidateQueries(['userPreferences']);
    }
  }, [queryClient, isAuthenticated]);

  const refreshSettings = useCallback(async () => {
    if (isAuthenticated) {
      try {
        await Promise.all([
          dispatch(fetchSettings()).unwrap(),
          dispatch(fetchCurrencies()).unwrap()
        ]);
      } catch (error) {
        console.error('Error refreshing settings:', error);
      }
    }
  }, [dispatch, isAuthenticated]);

  const value = {
    hospitalName: settings?.hospitalName || 'HMS Hospital',
    hospitalLogo: settings?.hospitalLogo || '',
    currency: settings?.currency || 'USD',
    currencySymbol: currencies.find(c => c.code === settings?.currency)?.symbol || '$',
    dateFormat: settings?.dateFormat || 'MM/DD/YYYY',
    timeFormat: settings?.timeFormat || '12',
    settings,
    currencies,
    initialized: isAuthenticated ? initialized : true, // Consider initialized if not authenticated
    refreshUserPreferences,
    refreshSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}; 